import React, { useState, useRef, RefObject, useEffect, CSSProperties } from "react";
import styled from "@emotion/styled";
import { Typography } from "@kaltura/ds-react-components";
import { HtmlReactParser } from "@kaltura/mediaspace-shared-utils";
import { Player, PlayerProps } from "@kaltura/mediaspace-shared-player";
import merge from "lodash/merge";
import { useMediaQuery } from "@kaltura/mediaspace-shared-utils";
import { systemWidth, useTheme } from "@kaltura/mediaspace-shared-styled";
import { generalHtmlStyles, TruncateWithTooltip } from "@kaltura/mediaspace-shared-ui";

export interface SingleVideoProps {
    playerConfig: PlayerProps; // only player v3 is supported
    removeNonBasicPlayerButtons?: boolean;
    headerText?: string;
    titleText?: string;
    descriptionHtml?: string;
    textBoxColor?: string;
    videoSide?: "left" | "right";
}

const StyledSingleVideo = styled("div")<{ maxHeight?: CSSProperties["maxHeight"] }>(
    ({ theme, maxHeight = "initial" }) => ({
        "&": systemWidth({ theme }),
        display: "flex",
        overflow: "hidden",
        maxHeight,
        [theme.breakpoints.down(theme.breakpoints.values.md)]: {
            flexDirection: "column",
            maxHeight: "initial",
        },
        borderRadius: theme.kaltura.shape.roundness1,
    })
);

const StylesTextbox = styled("div")<{ textBoxColor?: CSSProperties["color"] }>(({ theme, textBoxColor }) => ({
    backgroundColor: textBoxColor ? textBoxColor : theme.kaltura.palette.surfaces.paper,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    padding: theme.spacing(3),
    flexBasis: "1px",
    flexGrow: 1,
    flexShrink: 1,
    gap: theme.spacing(2),
    [theme.breakpoints.down(theme.breakpoints.values.md - 0.05)]: {
        flexDirection: "row",
        justifyContent: "flex-end",
    },
    [theme.breakpoints.down(theme.breakpoints.values.sm - 0.05)]: {
        flexDirection: "column",
    },
}));

const StyledTextBoxHeaders = styled("div")(({ theme }) => ({
    [theme.breakpoints.down(theme.breakpoints.values.md - 0.05)]: {
        flex: 1,
        alignSelf: "flex-start",
    },
    [theme.breakpoints.down(theme.breakpoints.values.sm - 0.05)]: {
        flex: 0,
    },
}));

const StyledHeaderText = styled("div")(({ theme }) => ({
    letterSpacing: "2px",
    fontSize: theme.typography.pxToRem(16),
    fontWeight: theme.kaltura.typography.fontWeightBold,
    textTransform: "uppercase",
    color: theme.kaltura.palette.tone2,
}));

const StyledHeaderTitle = styled(Typography)(({ theme }) => ({
    marginTop: theme.spacing(1),
    marginBottom: 0,
    textTransform: "capitalize",
}));

const StyledDescription = styled("div")(({ theme }) => ({
    color: theme.kaltura.palette.tone2,
    fontSize: theme.typography.pxToRem(16),
    lineHeight: theme.typography.pxToRem(21),
    overflow: "hidden",
    [theme.breakpoints.down(theme.breakpoints.values.md - 0.05)]: {
        flex: 1,
    },
    ...generalHtmlStyles(theme),
}));

const StyledPlayerContainer = styled("div")(({ theme }) => ({
    maxWidth: "initial",
    minWidth: "initial",

    [theme.breakpoints.up(theme.breakpoints.values.md)]: {
        maxWidth: "635px",
        minWidth: "635px",
    },
    [theme.breakpoints.up(theme.breakpoints.values.lg)]: {
        maxWidth: "789px",
        minWidth: "789px",
    },
    [theme.breakpoints.up(theme.breakpoints.values.xl)]: {
        maxWidth: "869px",
        minWidth: "869px",
    },
    [theme.breakpoints.down(theme.breakpoints.values.md - 0.05)]: {
        width: "100%",
    },
    flexBasis: "1px",
    flexGrow: 1,
    flexShrink: 0,
}));

const StyledPlayer = styled("div")({
    height: 0,
    position: "relative",
    paddingTop: "56.25%",
});

/**
 * Single video component shows configured text and description beside the video.
 */
export function SingleVideo(props: SingleVideoProps) {
    const {
        playerConfig,
        removeNonBasicPlayerButtons = false,
        headerText = "",
        titleText = "",
        descriptionHtml = "",
        textBoxColor = "",
        videoSide = "right",
    } = props;

    const playerElement: RefObject<HTMLDivElement> = useRef(null);
    const [maxHeight, setMaxHeight] = useState("initial");
    const [actualPlayerConfig, setActualPlayerConfig] = useState(playerConfig.config);

    const anyTextPresent = headerText || titleText || descriptionHtml;
    const theme = useTheme();
    const isLargeScreenOrAbove = useMediaQuery(theme.breakpoints.up(theme.breakpoints.values.lg));
    const isMediumScreenOrAbove = useMediaQuery(theme.breakpoints.up(theme.breakpoints.values.md));
    let numOfDescriptionLines = 5;
    let numOfTitleLines = 2;
    if (isLargeScreenOrAbove) {
        numOfTitleLines = 6;
        numOfDescriptionLines = 3;
    }
    else if (isMediumScreenOrAbove) {
        numOfTitleLines = 5;
        numOfDescriptionLines = 3;
    }

    useEffect(() => {
        playerElement.current?.clientHeight
            && playerElement.current?.clientHeight > 0
            && setMaxHeight(playerElement.current?.clientHeight + "px");
    }, [playerElement]);

    useEffect(() => {
        const getPlayerV3Config = () => {
            if (playerConfig.config) {
                const cfg = merge(
                    {},
                    playerConfig.config,
                    {
                        disableUserCache: true,
                    }
                );

                if (removeNonBasicPlayerButtons) {
                    cfg.ui = {
                        ...cfg.ui,
                        uiComponents: [
                            {
                                label: "niceComponent1", // just a name of your component
                                presets: ["Playback"], // the presets where you want to apply it
                                container: "BottomBarLeftControls",
                                get: () => undefined, // your component, in your case you just wanna return undefined
                                replaceComponent: "Forward", // name of component you wanna replace
                            },
                            {
                                label: "niceComponent2", // just a name of your component
                                presets: ["Playback"], // the presets where you want to apply it
                                container: "BottomBarLeftControls",
                                get: () => undefined, // your component, in your case you just wanna return undefined
                                replaceComponent: "Rewind", // name of component you wanna replace
                            },
                            {
                                label: "niceComponent3", // just a name of your component
                                presets: ["Playback"], // the presets where you want to apply it
                                container: "BottomBarRightControls",
                                get: () => undefined, // your component, in your case you just wanna return undefined
                                replaceComponent: "Settings", // name of component you wanna replace
                            },
                            {
                                label: "niceComponent4", // just a name of your component
                                presets: ["Playback"], // the presets where you want to apply it
                                container: "BottomBarRightControls",
                                get: () => undefined, // your component, in your case you just wanna return undefined
                                replaceComponent: "PictureInPicture", // name of component you wanna replace
                            },
                        ],
                    };
                }
                return cfg;
            }
            return playerConfig.config;
        };

        const config = getPlayerV3Config();
        setActualPlayerConfig(config);
    }, [playerConfig, removeNonBasicPlayerButtons]);

    // On desktop, video side is configurable. On mobile, video always goes first because of the vertical layout.
    const videoGoesFirst = !isMediumScreenOrAbove || videoSide === "left";

    const dataElement = anyTextPresent && (
        <StylesTextbox textBoxColor={textBoxColor} className="kms-ds-homepage-component-singlevideo-textbox">
            <StyledTextBoxHeaders className="kms-ds-homepage-component-singlevideo-header">
                {headerText && (
                    <TruncateWithTooltip text={headerText} lines={1}>
                        <StyledHeaderText className="kms-ds-homepage-component-singlevideo-headertext">
                            {headerText}
                        </StyledHeaderText>
                    </TruncateWithTooltip>
                )}
                {titleText && (
                    <TruncateWithTooltip text={titleText} lines={numOfTitleLines}>
                        <StyledHeaderTitle className="kms-ds-homepage-component-singlevideo-headertitle" variant={"h2"}>
                            {titleText}
                        </StyledHeaderTitle>
                    </TruncateWithTooltip>
                )}
            </StyledTextBoxHeaders>
            {descriptionHtml && (
                <TruncateWithTooltip text={HtmlReactParser(descriptionHtml)} lines={numOfDescriptionLines}>
                    <StyledDescription className="kms-ds-homepage-component-singlevideo-description">
                        {HtmlReactParser(descriptionHtml)}
                    </StyledDescription>
                </TruncateWithTooltip>
            )}
        </StylesTextbox>
    );

    return (
        <StyledSingleVideo maxHeight={maxHeight} className="kms-ds-homepage-component-singlevideo">
            {!videoGoesFirst && dataElement}

            <StyledPlayerContainer className="kms-ds-homepage-component-singlevideo-video">
                <StyledPlayer ref={playerElement}>
                    <Player media={playerConfig.media} config={actualPlayerConfig} />
                </StyledPlayer>
            </StyledPlayerContainer>

            {videoGoesFirst && dataElement}
        </StyledSingleVideo>
    );
}

export default SingleVideo;
